import StarIcon from '@mui/icons-material/Star';

import { Box, Card, CardContent, Chip, SxProps, Theme, Typography, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useCallback, useMemo } from 'react';

import { DEFAULT_CLAIM_LINK_TEMPLATE_COVER_IMAGE_URL } from '../../constants/shell';
import {
  CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH,
  CLAIM_LINK_TEMPLATE_CARD_THUMBNAIL_ASPECT_RATIO,
} from '../../constants/templates';
import { KeyboardKeysEnum } from '../../types/shell';
import { ClaimLinkTemplateStatusEnum, ITemplateCardConfig } from '../../types/templates';
import { isValidDate } from '../../utils/date';
import { getClaimLinkTemplateStatusChipProps, getTimeAgo } from '../../utils/templates';

interface IProps {
  thumbnailUrl?: string;
  title: string;
  description?: string;
  status?: ClaimLinkTemplateStatusEnum;
  actions?: React.ReactNode;
  isDefault?: boolean;
  isCampaignDefault?: boolean;
  isHighlighted?: boolean;
  updatedAt?: string;
  children?: React.ReactNode;
  config?: ITemplateCardConfig;
  onClick?: () => void;
  // Fill the grid track instead of capping at the fixed preview width.
  // Used in the templates grid (list + modal); single-preview cards keep the cap.
  fillWidth?: boolean;
}

const DEFAULT_BANNER_HEIGHT = 28;

const thumbnailContainerStyles = {
  pt: 3,
  px: 3,
  pb: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: grey[200],
};

const titleContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

export const ClaimLinkTemplateCardBase: React.FC<IProps> = ({
  thumbnailUrl,
  title,
  description,
  status,
  actions,
  isDefault,
  isCampaignDefault,
  isHighlighted = false,
  updatedAt,
  children,
  config,
  onClick,
  fillWidth = false,
}) => {
  const theme = useTheme();

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && (e.key === KeyboardKeysEnum.Enter || e.key === KeyboardKeysEnum.Space)) {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const shouldShowStatus = useMemo(() => config?.showStatusChip !== false, [config?.showStatusChip]);

  const hasDefaultBanner = useMemo(
    () => config?.showDefaultBanner !== false && (isCampaignDefault || isDefault),
    [config?.showDefaultBanner, isCampaignDefault, isDefault],
  );

  const cardStyles: SxProps<Theme> = useMemo(
    () => ({
      width: '100%',
      maxWidth: fillWidth ? 'none' : CLAIM_LINK_TEMPLATE_CARD_MAX_WIDTH,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      border: isHighlighted ? `2px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
      overflow: 'hidden',
      boxShadow: 'none',
    }),
    [theme, isHighlighted, fillWidth],
  );

  const getThumbnailStyles = useCallback(
    (imgUrl?: string): SxProps<Theme> => {
      const hasCoverImage = imgUrl && imgUrl.trim() !== '';
      const imageUrl = hasCoverImage ? imgUrl : DEFAULT_CLAIM_LINK_TEMPLATE_COVER_IMAGE_URL;

      return {
        width: '100%',
        aspectRatio: CLAIM_LINK_TEMPLATE_CARD_THUMBNAIL_ASPECT_RATIO,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: grey[200],
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        borderRadius: `${theme.spacing(0.5)} ${theme.spacing(0.5)} 0 0`,
        boxShadow: theme.shadows[2],
      };
    },
    [theme],
  );

  const orgDefaultBannerStyles = useMemo(
    () => ({
      width: '100%',
      height: DEFAULT_BANNER_HEIGHT,
      backgroundColor: theme.wl?.portal_accent_color || theme.palette.primary.main,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1,
      opacity: 0.8,
    }),
    [theme.palette],
  );

  const cardContentStyles = useMemo(
    () => ({
      p: 2,
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.common.white,
      '&:last-child': { paddingBottom: 2 },
    }),
    [theme.palette],
  );

  const titleStyles = useMemo(
    () => ({
      fontSize: 16,
      fontWeight: theme.typography.fontWeightMedium,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: 1,
      minWidth: 0,
    }),
    [theme.typography],
  );

  const descriptionStyles = useMemo(
    () => ({
      fontSize: 14,
      color: 'text.secondary',
      fontWeight: theme.typography.fontWeightMedium,
      mb: actions ? 2 : 0,
    }),
    [actions, theme.typography],
  );

  const renderDefaultBanner = useCallback(() => {
    if (!hasDefaultBanner) {
      return null;
    }

    const templateLabel = isCampaignDefault ? 'Campaign default' : 'Organization default';
    const bannerDataAction = isCampaignDefault ? 'campaign_default_banner' : 'org_default_banner';

    return (
      <Box sx={orgDefaultBannerStyles} data-action={bannerDataAction}>
        <Typography fontWeight="medium" color={theme.palette.common.white} sx={{ fontSize: 13 }}>
          {templateLabel}
        </Typography>
        <StarIcon
          sx={{
            fontSize: 16,
            color: theme.palette.common.white,
            position: 'absolute',
            right: 16,
          }}
        />
      </Box>
    );
  }, [hasDefaultBanner, isCampaignDefault, orgDefaultBannerStyles, theme]);

  const statusChipData = useMemo(
    () => getClaimLinkTemplateStatusChipProps(status as ClaimLinkTemplateStatusEnum, theme),
    [status, theme],
  );

  const descriptionContent = useMemo(() => {
    if (description) {
      return description;
    }

    if (!updatedAt) {
      return undefined;
    }

    if (!isValidDate(new Date(updatedAt))) {
      return 'Edited just now';
    }
    return getTimeAgo(updatedAt);
  }, [description, updatedAt]);

  return (
    <Card
      sx={{ ...cardStyles, ...(onClick && { cursor: 'pointer' }) }}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      data-action="claim_link_template_card"
      {...(onClick && { role: 'button', tabIndex: 0, 'aria-label': `Select template ${title}` })}
    >
      {renderDefaultBanner()}
      <Box sx={thumbnailContainerStyles}>
        <Box sx={getThumbnailStyles(thumbnailUrl)} />
      </Box>
      <CardContent sx={cardContentStyles}>
        <Box>
          <Box sx={titleContainerStyles}>
            <Typography sx={titleStyles} title={title}>
              {title}
            </Typography>
            {shouldShowStatus && statusChipData && (
              <Chip
                label={statusChipData.label}
                size="small"
                variant="filled"
                sx={{
                  flexShrink: 0,
                  backgroundColor: statusChipData.backgroundColor,
                  color: statusChipData.color,
                  border: `1px solid ${theme.palette.common.white}`,
                  '& .MuiChip-label': {
                    px: 1.25,
                    py: 0.5,
                  },
                }}
                data-action={statusChipData.dataAction}
              />
            )}
          </Box>
          {descriptionContent && <Typography sx={descriptionStyles}>{descriptionContent}</Typography>}
        </Box>
        {actions && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
              {actions}
            </Box>
          </Box>
        )}
      </CardContent>
      {children && (
        <Box onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {children}
        </Box>
      )}
    </Card>
  );
};
